import {
  Anime,
  AnimeEpisode,
  AnimeSource,
  AnimeSeason,
  BroadcastDay,
  BROADCAST_DAYS,
  LineNotifyConfig,
  TIER_CONFIGS,
  WakuFeedItem,
} from '../types/anime';
import {
  areAnimeMatching,
  detectPlatformFromUrl,
  getSourceAudio,
  getTodayBroadcastDay,
  getTodayKey,
  hasConflictingSource,
  isCompilationCount,
  isGenericThaiTitle,
  withSourceAudio,
} from '../utils/helpers';
import type { RukaScheduleEntry } from '../utils/rukaSchedule';

export interface WakuDetailResponse {
  url: string;
  fullTitle: string;
  displayTitle: string;
  romajiTitle: string;
  thaiTitle: string;
  latestEp: number;
  totalEpisodes: number;
  status: 'กำลังฉาย' | 'จบแล้ว';
  audioType: 'ซับไทย' | 'พากย์ไทย' | 'ซับ+พากย์';
  imageUrl: string;
  synopsis: string;
  season: AnimeSeason;
  year: number;
  wakuUpdatedIso?: string;
  broadcastDay?: BroadcastDay;
  broadcastTime?: string;
  updatePatternText?: string;
  vipPolicyText?: string;
  episodes: AnimeEpisode[];
}

export function parseWakuIsoToDayTime(isoStr?: string): {
  dayId: BroadcastDay;
  dayNameTh: string;
  timeStr: string;
  dateStrTh: string;
} | null {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;

  const jsDay = d.getDay();
  const foundDay = BROADCAST_DAYS.find((b) => b.jsDayIndex === jsDay);
  const dayId: BroadcastDay = foundDay ? foundDay.id : 'SAT';
  const dayNameTh = foundDay ? foundDay.nameTh : 'วันเสาร์';

  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const timeStr = `${hh}:${mm}`;

  const dateStrTh = d.toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return { dayId, dayNameTh, timeStr, dateStrTh };
}

export interface JikanAnimeItem {
  mal_id: number;
  title: string;
  title_english?: string;
  title_japanese?: string;
  images?: {
    webp?: { large_image_url?: string };
    jpg?: { large_image_url?: string };
  };
  episodes?: number;
  status?: string;
  score?: number;
  season?: string;
  year?: number;
  broadcast?: {
    day?: string;
    time?: string;
    timezone?: string;
  };
  synopsis?: string;
}

export async function fetchWakuRecent(page = 1): Promise<WakuFeedItem[]> {
  try {
    const res = await fetch(`/api/waku/recent?page=${page}`, { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function fetchWakuAllAiring(forceRefresh = false): Promise<WakuFeedItem[]> {
  try {
    const res = await fetch(`/api/waku/recent?mode=airing${forceRefresh ? '&refresh=1' : ''}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function searchWakuAnime(query: string): Promise<WakuFeedItem[]> {
  if (!query.trim()) return [];
  try {
    const res = await fetch(`/api/waku/search?q=${encodeURIComponent(query.trim())}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function fetchRukaAllAiring(forceRefresh = false): Promise<WakuFeedItem[]> {
  try {
    const res = await fetch(`/api/ruka/recent${forceRefresh ? '?refresh=1' : ''}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

/** AnimeRuka's weekly schedule (sub/dub slot per show) — the app's broadcast times follow it */
export async function fetchRukaScheduleEntries(): Promise<RukaScheduleEntry[]> {
  try {
    const res = await fetch('/api/ruka/schedule', { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.entries) ? data.entries : [];
  } catch {
    return [];
  }
}

export async function searchRukaAnime(query: string): Promise<WakuFeedItem[]> {
  if (!query.trim()) return [];
  try {
    const res = await fetch(`/api/ruka/search?q=${encodeURIComponent(query.trim())}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function fetchAniOneRecent(): Promise<WakuFeedItem[]> {
  try {
    const res = await fetch('/api/anione/recent', { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function fetchBilibiliRecent(): Promise<WakuFeedItem[]> {
  try {
    const res = await fetch('/api/bilibili/recent', { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function searchAniOneAnime(query: string): Promise<WakuFeedItem[]> {
  if (!query.trim()) return [];
  try {
    const res = await fetch(`/api/anione/search?q=${encodeURIComponent(query.trim())}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

/** One fetch of a feed shared by every show checked in the next few minutes */
function sharedFeed(load: () => Promise<WakuFeedItem[]>, ttlMs = 5 * 60 * 1000) {
  let cached: { at: number; items: Promise<WakuFeedItem[]> } | null = null;
  return () => {
    if (!cached || Date.now() - cached.at > ttlMs) cached = { at: Date.now(), items: load().catch(() => []) };
    return cached.items;
  };
}

const LINK_PLATFORMS: Array<{
  type: 'bilibili' | 'anime-waku' | 'youtube' | 'animeruka';
  name: string;
  search: (q: string) => Promise<WakuFeedItem[]>;
  /**
   * The platform's airing list, matched as well as the search: a search only finds titles that
   * contain the query word for word, so a spelling difference ("พลังจิด" vs "พลังจิต") hides
   * the show, while the title matcher handles known spelling variants.
   */
  feed?: () => Promise<WakuFeedItem[]>;
}> = [
  { type: 'bilibili', name: 'Bilibili', search: (q) => searchBilibiliAnime(q) },
  { type: 'anime-waku', name: 'Anime-Waku', search: (q) => searchWakuAnime(q) },
  { type: 'youtube', name: 'Ani-One', search: (q) => searchAniOneAnime(q), feed: sharedFeed(fetchAniOneRecent) },
  { type: 'animeruka', name: 'AnimeRuka', search: (q) => searchRukaAnime(q) },
];

/** Platforms the background link-filler looks for on every saved show */
export const LINKED_PLATFORM_TYPES = LINK_PLATFORMS.map((p) => p.type);

/** Title without season/part markers, for a broad search ("…ดาบ (ภาค2)" → "…ดาบ") */
function searchTermOf(title?: string): string {
  return (title || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/(?:season|ซีซั่?น|ภาค(?:ที่)?|part)\s*\d+/gi, ' ')
    .replace(/\d+(?:st|nd|rd|th)\s*season/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Search each platform's whole catalogue for this same show + season and return the links the
 * anime doesn't have yet. The airing feeds alone miss finished seasons (Reincarnated as a Sword
 * S1 is on Bilibili, but not in its weekly timeline). Only platforms with no link are searched.
 */
export async function findCrossPlatformLinks(anime: Anime): Promise<AnimeSource[]> {
  const sources = anime.sources || [];
  const missing = LINK_PLATFORMS.filter((p) => !sources.some((s) => s.type === p.type));
  if (missing.length === 0) return [];

  const thai = isGenericThaiTitle(anime.thaiTitle) ? '' : anime.thaiTitle;
  const terms = [...new Set([searchTermOf(thai), searchTermOf(anime.title)].filter((t) => t.length >= 2))];
  if (terms.length === 0) return [];

  const found: AnimeSource[] = [];
  const known = () => [...sources, ...found];
  for (const platform of missing) {
    const results = (
      await Promise.all([
        ...terms.map((t) => platform.search(t).catch(() => [] as WakuFeedItem[])),
        platform.feed ? platform.feed() : Promise.resolve([] as WakuFeedItem[]),
      ])
    ).flat();
    for (const item of results) {
      if (known().some((s) => s.url === item.url)) continue;
      // Titles must name the same show and season (no URL shortcut: compare titles only)
      if (!areAnimeMatching({ title: anime.title, thaiTitle: anime.thaiTitle }, item)) continue;
      const withPlatform = { ...item, platform: platform.type };
      if (hasConflictingSource({ sources: known() }, withPlatform)) continue;
      const eps = known().map((s) => Number(s.latestEp) || 0);
      if (isCompilationCount(Number(item.latestEp) || 0, Number(anime.totalEp) || 0, eps)) continue;
      found.push(
        withSourceAudio({
          type: platform.type,
          name: platform.name,
          url: item.url,
          latestEp: Number(item.latestEp) || 0,
          audio: item.audioType === 'พากย์ไทย' ? 'พากย์ไทย' : 'ซับไทย',
        })
      );
    }
  }
  return found;
}

export async function searchBilibiliAnime(query: string): Promise<WakuFeedItem[]> {
  if (!query.trim()) return [];
  try {
    const res = await fetch(`/api/bilibili/search?q=${encodeURIComponent(query.trim())}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function fetchWeTvRecent(): Promise<WakuFeedItem[]> {
  try {
    const res = await fetch('/api/wetv/recent', { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

export async function searchWeTvAnime(query: string): Promise<WakuFeedItem[]> {
  if (!query.trim()) return [];
  try {
    const res = await fetch(`/api/wetv/search?q=${encodeURIComponent(query.trim())}`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

let platformFeedsCache: {
  anione: WakuFeedItem[];
  bilibili: WakuFeedItem[];
  waku: WakuFeedItem[];
  wetv: WakuFeedItem[];
  ruka: WakuFeedItem[];
  all: WakuFeedItem[];
  timestamp: number;
} | null = null;

export async function getAllPlatformFeeds(forceRefresh = false): Promise<{
  anione: WakuFeedItem[];
  bilibili: WakuFeedItem[];
  waku: WakuFeedItem[];
  wetv: WakuFeedItem[];
  ruka: WakuFeedItem[];
  all: WakuFeedItem[];
}> {
  const now = Date.now();
  if (!forceRefresh && platformFeedsCache && now - platformFeedsCache.timestamp < 60_000) {
    return platformFeedsCache;
  }

  const [anioneRes, biliRes, wakuRes, wetvRes, rukaRes] = await Promise.allSettled([
    fetchAniOneRecent(),
    fetchBilibiliRecent(),
    fetchWakuAllAiring(forceRefresh),
    fetchWeTvRecent(),
    fetchRukaAllAiring(forceRefresh),
  ]);

  const anione = anioneRes.status === 'fulfilled' ? anioneRes.value : [];
  const bilibili = biliRes.status === 'fulfilled' ? biliRes.value : [];
  const waku = wakuRes.status === 'fulfilled' ? wakuRes.value : [];
  const wetv = wetvRes.status === 'fulfilled' ? wetvRes.value : [];
  const ruka = rukaRes.status === 'fulfilled' ? rukaRes.value : [];

  const all = [...anione, ...bilibili, ...waku, ...wetv, ...ruka];

  platformFeedsCache = {
    anione,
    bilibili,
    waku,
    wetv,
    ruka,
    all,
    timestamp: now,
  };

  return platformFeedsCache;
}

/**
 * True unless the source page clearly belongs to a different show
 * (e.g. a WeTV link for "Goblin Slayer" attached to "Reborn as a Goblin").
 * Pages that return no title can't be judged, so they count as a match.
 */
export function isDetailForAnime(
  anime: Pick<Anime, 'title' | 'thaiTitle'>,
  detail: Pick<WakuDetailResponse, 'fullTitle' | 'displayTitle' | 'romajiTitle' | 'thaiTitle'>
): boolean {
  const titles = [detail.fullTitle, detail.displayTitle, detail.romajiTitle].filter(
    (t) => t && t.trim()
  );
  if (titles.length === 0) return true;
  return areAnimeMatching(
    { title: anime.title, thaiTitle: anime.thaiTitle },
    {
      fullTitle: detail.fullTitle,
      displayTitle: detail.displayTitle,
      romajiTitle: detail.romajiTitle,
      thaiTitle: detail.thaiTitle,
    }
  );
}

function urlHost(url?: string): string {
  try {
    return new URL(url || '').host;
  } catch {
    return '';
  }
}

/** Drop episodes that don't come from any of the anime's remaining source sites */
export function keepEpisodesFromSources(
  episodes: AnimeEpisode[],
  sources: { url: string }[]
): AnimeEpisode[] {
  const hosts = new Set(sources.map((s) => urlHost(s.url)).filter(Boolean));
  return episodes.filter((ep) => hosts.has(urlHost(ep.url)));
}

/** Combine episode lists from several sources (e.g. Waku sub page + dub page), newest data wins */
export function mergeEpisodeLists(...lists: AnimeEpisode[][]): AnimeEpisode[] {
  const byKey = new Map<string, AnimeEpisode>();
  for (const list of lists) {
    for (const ep of list || []) {
      byKey.set(ep.url || `${ep.audio}:${ep.number}`, ep);
    }
  }
  return [...byKey.values()].sort(
    (a, b) => a.number - b.number || (a.audio || '').localeCompare(b.audio || '')
  );
}

/** "ซับ+พากย์" when the anime has both a sub and a dub link */
export function audioTypeFromSources(
  sources: { url?: string; name?: string; audio?: string }[],
  fallback: Anime['audioType']
): Anime['audioType'] {
  const audios = new Set(sources.map((s) => getSourceAudio(s)));
  if (audios.has('ซับไทย') && audios.has('พากย์ไทย')) return 'ซับ+พากย์';
  return fallback;
}

export async function fetchWakuDetail(url: string): Promise<WakuDetailResponse | null> {
  if (!url.trim()) return null;
  try {
    const res = await fetch(`/api/waku/detail?url=${encodeURIComponent(url.trim())}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data && !data.error) return data as WakuDetailResponse;
    return null;
  } catch {
    return null;
  }
}

export async function searchJikanAnime(query: string): Promise<JikanAnimeItem[]> {
  if (!query.trim()) return [];
  try {
    const res = await fetch(
      `https://api.jikan.moe/v4/anime?q=${encodeURIComponent(query.trim())}&limit=5&sfw=true`
    );
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.data) ? json.data : [];
  } catch {
    return [];
  }
}

export function mapJikanSeason(season?: string): AnimeSeason | null {
  if (!season) return null;
  const s = season.toLowerCase();
  if (s === 'winter') return 'WINTER';
  if (s === 'spring') return 'SPRING';
  if (s === 'summer') return 'SUMMER';
  if (s === 'fall' || s === 'autumn') return 'FALL';
  return null;
}

export function mapJikanBroadcastDay(dayStr?: string): BroadcastDay {
  if (!dayStr) return 'UNKNOWN';
  const d = dayStr.toLowerCase();
  if (d.startsWith('mon')) return 'MON';
  if (d.startsWith('tue')) return 'TUE';
  if (d.startsWith('wed')) return 'WED';
  if (d.startsWith('thu')) return 'THU';
  if (d.startsWith('fri')) return 'FRI';
  if (d.startsWith('sat')) return 'SAT';
  if (d.startsWith('sun')) return 'SUN';
  return 'UNKNOWN';
}

export async function checkSingleAnimeUpdate(anime: Anime): Promise<{
  updated: boolean;
  newLatestEp: number;
  previousEp: number;
  updatedAnime: Anime;
}> {
  let highestEp = Number(anime.latestEp) || 0;
  const previousEp = highestEp;
  const updatedSources = [...(anime.sources || [])];
  let episodes = anime.episodes || [];
  const freshEpisodeLists: AnimeEpisode[][] = [];
  let synopsis = anime.synopsis || '';
  let totalEp = anime.totalEp || 0;
  let audioType = anime.audioType;
  let status = anime.status;
  let imageUrl = anime.imageUrl;
  let broadcastDay = anime.broadcastDay;
  let broadcastTime = anime.broadcastTime;
  // Latest episode each source reports right now (used to undo numbers a wrong source inflated)
  const liveLatestByUrl = new Map<string, number>();
  let liveFinished = false;

  // 1. Check all existing sources (Anime-Waku, Bilibili, WeTV, YouTube Playlist)
  for (let i = 0; i < updatedSources.length; i++) {
    const src = updatedSources[i];
    if (!src.url) continue;

    if (
      src.url.includes('anime-waku.com') ||
      src.url.includes('animeruka.com') ||
      src.url.includes('bilibili.tv') ||
      src.url.includes('wetv.vip') ||
      src.url.includes('youtube.com/playlist')
    ) {
      const detail = await fetchWakuDetail(src.url);
      const otherEps = updatedSources
        .filter((_, j) => j !== i)
        .map((s) => Number(s.latestEp) || 0);
      if (
        detail &&
        (!isDetailForAnime(anime, detail) ||
          isCompilationCount(detail.latestEp, Number(anime.totalEp) || 0, otherEps))
      ) {
        // Wrong show linked (title mismatch), or a franchise-wide compilation of several
        // seasons — remove it so its episodes/counts can't leak into this season
        updatedSources.splice(i, 1);
        i--;
        continue;
      }
      if (detail) {
        if (detail.latestEp > 0) liveLatestByUrl.set(src.url, detail.latestEp);
        if (detail.status === 'จบแล้ว') liveFinished = true;
        if (detail.latestEp > src.latestEp) {
          updatedSources[i] = { ...src, latestEp: detail.latestEp };
        }
        if (detail.latestEp > highestEp) {
          highestEp = detail.latestEp;
        }
        if (detail.episodes && detail.episodes.length > 0) {
          freshEpisodeLists.push(detail.episodes);
        }
        if (detail.audioType) {
          audioType = detail.audioType;
        }
        if (detail.synopsis && !synopsis) {
          synopsis = detail.synopsis;
        }
        if (detail.totalEpisodes && !totalEp) {
          totalEp = detail.totalEpisodes;
        }

        if (detail.status === 'จบแล้ว') {
          status = 'จบแล้ว';
        }
        if (detail.imageUrl && !imageUrl) {
          imageUrl = detail.imageUrl;
        }
        if (detail.broadcastDay && detail.broadcastDay !== 'UNKNOWN' && broadcastDay === 'UNKNOWN') {
          broadcastDay = detail.broadcastDay;
        }
        if (detail.broadcastTime && !broadcastTime) {
          broadcastTime = detail.broadcastTime;
        }
      }
    } else if (src.url.includes('youtube.com') || src.url.includes('youtu.be')) {
      try {
        const res = await fetch(`/api/youtube/check?url=${encodeURIComponent(src.url)}`);
        if (res.ok) {
          const ytData = await res.json();
          if (ytData.latestEp && ytData.latestEp > src.latestEp) {
            updatedSources[i] = { ...src, latestEp: ytData.latestEp };
          }
          if (ytData.latestEp && ytData.latestEp > highestEp) {
            highestEp = ytData.latestEp;
          }
        }
      } catch {}
    }
  }

  // 2. If no anime-waku source yet, try auto-searching on anime-waku by title
  const hasWakuSource = updatedSources.some((s) => s.url.includes('anime-waku.com'));
  if (!hasWakuSource && anime.title) {
    const foundList = await searchWakuAnime(anime.title);
    // Only accept a search hit that is actually this show (not just a similar keyword)
    const best = foundList.find((f) => areAnimeMatching(anime, f));
    if (best) {
      updatedSources.push({
        type: 'anime-waku',
        name: 'Anime-Waku',
        url: best.url,
        latestEp: best.latestEp || highestEp,
      });
      if (best.latestEp > highestEp) {
        highestEp = best.latestEp;
      }
      if (!imageUrl && best.imageUrl) {
        imageUrl = best.imageUrl;
      }
    }
  }

  // Sub and dub pages each have their own episode list — keep both
  if (freshEpisodeLists.length > 0) {
    episodes = mergeEpisodeLists(...freshEpisodeLists);
  }
  audioType = audioTypeFromSources(updatedSources, audioType);

  // If a wrong source was removed, discard its episodes and recompute the latest episode
  if (updatedSources.length < (anime.sources || []).length) {
    episodes = keepEpisodesFromSources(episodes, updatedSources);
    for (let i = 0; i < updatedSources.length; i++) {
      const live = liveLatestByUrl.get(updatedSources[i].url);
      if (live) updatedSources[i] = { ...updatedSources[i], latestEp: live };
    }
    const liveValues = [...liveLatestByUrl.values()];
    highestEp = liveValues.length
      ? Math.max(...liveValues)
      : Math.max(0, ...episodes.map((e) => Number(e.number) || 0));
    status = liveFinished ? 'จบแล้ว' : 'กำลังฉาย';
  }

  const hasNewEp = highestEp > previousEp;
  const todayDayId = getTodayBroadcastDay();
  const todayDayName =
    BROADCAST_DAYS.find((d) => d.id === todayDayId)?.nameTh || 'วันนี้';

  const updatedAnime: Anime = {
    ...anime,
    latestEp: highestEp,
    totalEp: totalEp || anime.totalEp,
    audioType,
    status,
    imageUrl,
    synopsis,
    broadcastDay,
    broadcastTime,
    sources: updatedSources,
    episodes,
    lastUpdatedAt: hasNewEp ? getTodayKey() : anime.lastUpdatedAt,
    detectedDayNote:
      hasNewEp && broadcastDay === 'UNKNOWN'
        ? `ตรวจพบตอนที่ ${highestEp} อัปเดตเมื่อ${todayDayName} (${getTodayKey()})`
        : anime.detectedDayNote,
  };

  return {
    updated: hasNewEp,
    newLatestEp: highestEp,
    previousEp,
    updatedAnime,
  };
}

/**
 * Hand the alert settings to the server, which checks every 20 minutes and sends the alerts
 * itself (so they arrive with the site closed). Returns whether the server is now sending.
 */
export async function saveServerAlertConfig(config: LineNotifyConfig): Promise<boolean> {
  try {
    const res = await fetch('/api/alerts/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    const data = await res.json();
    return Boolean(res.ok && data?.serverAlerts);
  } catch {
    return false;
  }
}

/**
 * Watch buttons on an alert, only for the platforms the show is linked to, in a fixed order:
 * Anime-Waku (where it's usually watched) as the big one, then AnimeRuka, Bilibili, Ani-One.
 * Mirrors watchLinksOf in the API.
 */
const ALERT_PLATFORMS: Array<{ type: string; name: string; search: (q: string) => string }> = [
  { type: 'anime-waku', name: 'Anime-Waku', search: (q) => `https://anime-waku.com/?s=${q}` },
  { type: 'animeruka', name: 'AnimeRuka', search: (q) => `https://animeruka.com/?s=${q}` },
  { type: 'bilibili', name: 'Bilibili', search: (q) => `https://www.bilibili.tv/th/search-result?q=${q}` },
  { type: 'youtube', name: 'Ani-One', search: (q) => `https://www.youtube.com/@AniOneThailand/search?query=${q}` },
];

export function alertWatchLinks(sources: AnimeSource[] = [], title = '') {
  const query = encodeURIComponent(title.replace(/[\u0E00-\u0E7F]+/g, ' ').replace(/\s+/g, ' ').trim() || title);
  return ALERT_PLATFORMS.map((p) => {
    const linked = sources.find(
      (s) => s.url?.startsWith('http') && detectPlatformFromUrl(s.url).type === p.type
    );
    return { type: p.type, name: p.name, url: linked ? linked.url : p.search(query), linked: Boolean(linked) };
  }).filter((l) => l.linked);
}

/** Whose schedule a card's day/time follows, as shown on the alert */
export const SCHEDULE_FROM: Record<string, string> = { animeruka: 'AnimeRuka', anione: 'Ani-One' };

/** LINE only shows JPEG/PNG under 10MB: ask Bilibili for a 600px JPEG instead of its huge PNG / WebP */
function lineImageUrl(url?: string): string {
  const raw = (url || '').trim();
  if (/^https:\/\/p(?:ic)?\.bstarstatic\.com\//i.test(raw)) return `${raw.replace(/@.*$/, '')}@600w.jpg`;
  return raw;
}

export async function sendLineNotification(
  config: LineNotifyConfig,
  options: {
    textMessage?: string;
    anime?: Anime;
    badge?: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const anime = options.anime;
    const primarySource = anime?.sources?.[0];
    const dayObj = anime
      ? BROADCAST_DAYS.find((d) => d.id === anime.broadcastDay)
      : undefined;

    const scheduleText = anime
      ? anime.broadcastDay === 'UNKNOWN'
        ? `อัปเดตบนเว็บ ${primarySource?.name || 'Anime-Waku'} (${anime.audioType})`
        : `ออกอากาศทุก${dayObj?.nameTh || ''} ${anime.broadcastTime ? `เวลา ${anime.broadcastTime} น.` : ''} (${anime.audioType})`
      : '';

    const tier = anime ? TIER_CONFIGS[anime.grade] : undefined;
    const watched = Number(anime?.watchedEp) || 0;
    const aired = Number(anime?.latestEp) || 0;
    const savedTotal = Number(anime?.totalEp) || 0;
    const total = savedTotal > 0 && aired <= savedTotal ? savedTotal : 0;
    const complete = aired > 0 && (anime?.status === 'จบแล้ว' || (total > 0 && aired >= total));
    const countsText = complete
      ? `ดูแล้ว ${watched} · ฉายจบครบ ${Math.max(total, aired)} ตอน`
      : `ดูแล้ว ${watched} · ฉายแล้ว ${aired}${total ? `/${total}` : ''} ตอน`;

    const animeCard = anime
      ? {
          title: anime.title,
          thaiTitle: anime.thaiTitle && !anime.title.includes(anime.thaiTitle) ? anime.thaiTitle : '',
          grade: anime.grade,
          status: anime.status,
          tierLabel: tier ? `${tier.title} · ${tier.subtitle.split(' / ')[0]}` : '',
          watched,
          aired,
          total,
          ep: aired,
          audioType: anime.audioType,
          platform: primarySource?.name || 'Anime-Waku',
          watchUrl: primarySource?.url || 'https://anime-waku.com/',
          links: alertWatchLinks(anime.sources, anime.title),
          scheduleDay: anime.broadcastDay === 'UNKNOWN' ? '' : dayObj?.nameTh || '',
          scheduleTime: anime.broadcastTime || '',
          scheduleFrom: SCHEDULE_FROM[anime.scheduleSource || ''] || '',
          imageUrl: lineImageUrl(anime.imageUrl),
          badge: options.badge || '🔔 แจ้งเตือนอนิเมะ',
          scheduleText,
        }
      : undefined;

    const defaultText = anime
      ? [
          `🔔 ${anime.title}${anime.thaiTitle ? ` (${anime.thaiTitle})` : ''}`,
          `🏆 ${tier?.title || anime.grade}`,
          `📺 ${countsText}`,
          `🗓️ ${scheduleText}`,
          `🔗 ดูได้ที่: ${primarySource?.url || 'https://anime-waku.com/'}`,
        ].join('\n')
      : options.textMessage || '🔔 ทดสอบการแจ้งเตือนจาก Anime Ranker!';

    const res = await fetch('/api/notify-line', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: config.mode,
        channelAccessToken: config.channelAccessToken,
        targetId: config.targetId,
        webhookUrl: config.webhookUrl,
        textMessage: options.textMessage || defaultText,
        // Only a hand-written message (e.g. today's schedule list) goes above the Discord card
        summaryText: options.textMessage || '',
        animeCard,
      }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return {
        success: false,
        error: data?.error || data?.response || `HTTP ${res.status}`,
      };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}
